import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import { CoreCommonModule } from "@core/common.module";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "@core/services/auth";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { first } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { RegistrationHelpPageComponent } from "app/main/pages/registration-help-page/registration-help-page.component";
import { OtpInputComponent } from "./otp-input/otp-input";

@Component({
  standalone: true,
  imports: [CoreCommonModule, NgbModule, OtpInputComponent],
  selector: "app-forgot-password",
  templateUrl: "./forgot-password.html",
  styleUrls: ["./forgot-password.scss"],
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  @Input() username: string;
  @Input() prefilledPhoneNumber = "";
  @Input() phoneNumberReadonly = false;

  @Output() back = new EventEmitter<void>();
  @Output() passwordResetComplete = new EventEmitter<string>();

  @ViewChild("otpInputRef") otpInputRef: OtpInputComponent;

  private _formBuilder = inject(UntypedFormBuilder);
  private _authenticationService = inject(AuthenticationService);
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private destroyRef = inject(DestroyRef);

  forgotPasswordForm: UntypedFormGroup;
  f_submitted = false;
  loading = false;
  error: any = {};

  phoneNumberConfirmation = false;
  resetCodeGenerated = false;
  newPasswords = false;
  proceedResetPassword = false;
  passwordUpdatedSuccess = false;
  passwordSubmitted = false;
  codeSubmitted = false;

  otpInvalid = false;
  otpErrorMessage = "";
  currentOtpCode = "";
  verifiedOtpCode = "";

  resendCountDown = 90;
  resendInterval: any = null;
  canResend = false;

  passwordTextType = false;
  passwordTextType2 = false;

  resetCodeHint = "Enter the code sent to the number you provided.";
  flagImageUrl = "assets/images/pages/flag_of_Kenya.png";
  successTick = "assets/img/tick.png";

  ngOnInit(): void {
    this.forgotPasswordForm = this._formBuilder.group({
      phoneNumber: [this.prefilledPhoneNumber || "", [Validators.required]],
      resetCode: [""],
      password: [""],
      confirmPassword: [""],
    });
    this.phoneNumberConfirmation = true;
  }

  ngOnDestroy(): void {
    if (this.resendInterval) clearInterval(this.resendInterval);
  }

  get ff() {
    return this.forgotPasswordForm.controls;
  }

  clearAlerts(): void {
    this.error = {};
  }

  toggleCodeValidator(): void {
    this.ff.resetCode.setValidators([Validators.required]);
    this.ff.resetCode.updateValueAndValidity();
  }

  onSubmitForgot(): void {
    this.f_submitted = true;
    if (this.forgotPasswordForm.invalid) return;

    this.loading = true;
    this.clearAlerts();

    this._authenticationService
      .requestPasswordReset(this.username)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.showSuccess(res.message);
          this.resetCodeGenerated = true;
          this.startResendCountdown();
          this.toggleCodeValidator();
          this.proceedResetPassword = true;
        },
        error: (err) => {
          this.loading = false;
          this.error.title = "Error";
          this.error.body = err.error?.message || "Something went wrong. Please try again.";
        },
      });
  }

  startResendCountdown(): void {
    this.resendCountDown = 90;
    this.canResend = false;
    this.resendInterval = setInterval(() => {
      this.resendCountDown--;
      if (this.resendCountDown <= 0) {
        clearInterval(this.resendInterval);
        this.canResend = true;
      }
    }, 1000);
  }

  get formattedCountdown(): string {
    const minutes = Math.floor(this.resendCountDown / 60);
    const seconds = this.resendCountDown % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  resendCode(): void {
    this.loading = false;
    this.currentOtpCode = "";
    this.otpInputRef?.clear();
    this.ff.resetCode.setValue("");
    this.codeSubmitted = false;
    this.newPasswords = false;
    this.clearAlerts();

    this._authenticationService
      .requestPasswordReset(this.username)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.showSuccess(res.message);
          this.resetCodeGenerated = true;
          this.startResendCountdown();
        },
        error: (err) => {
          this.loading = false;
          this.error.title = "Error";
          this.error.body = err.error?.message || "Something went wrong. Please try again.";
        },
      });
  }

  verifyOtp(): void {
    this.codeSubmitted = true;
    if (this.currentOtpCode.length < 6) return;

    this.ff.resetCode.setValue(this.currentOtpCode);
    this.verifiedOtpCode = this.currentOtpCode;
    this.clearAlerts();
    this.loading = true;

    this._authenticationService
      .verifyResetCode(this.username, this.ff.resetCode.value)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false;
          this.ff.password.setValue("");
          this.ff.confirmPassword.setValue("");
          this.resetCodeGenerated = false;
          this.newPasswords = true;
        },
        error: (err) => {
          this.loading = false;
          this.otpInvalid = true;
          this.otpErrorMessage = err?.error?.message || "Invalid code. Please try again.";
          this.currentOtpCode = "";
          this.otpInputRef?.clear();
        },
      });
  }

  changePassword(): void {
    this.passwordSubmitted = true;
    if (this.ff.password.value !== this.ff.confirmPassword.value) return;

    const password = this.ff.password.value;
    const hasMinLength = password.length >= 4;
    const hasNumber = /\d/.test(password);
    const hasUppercase = /[A-Z]/.test(password);

    if (!hasMinLength || !hasNumber || !hasUppercase) {
      this.error.body =
        "Password must be at least 4 characters, contain at least one number and one uppercase letter";
      this.ff.password.setValue("");
      this.ff.confirmPassword.setValue("");
      return;
    }

    this.loading = true;
    const changePasswordDetails = {
      newPassword: this.ff.password.value,
      confirmPassword: this.ff.confirmPassword.value,
      resetCode: this.verifiedOtpCode,
    };

    this._authenticationService
      .confirmPasswordReset(this.username, changePasswordDetails)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearAlerts();
          this.proceedResetPassword = false;
          this.passwordUpdatedSuccess = true;
          setTimeout(() => {
            this.passwordSubmitted = false;
            this.loading = false;
            this.passwordUpdatedSuccess = false;
            this.newPasswords = false;
            this.resetCodeGenerated = false;
            this.toastr.success("Password reset success! Use your new password", "", {
              timeOut: 5000,
              toastClass: "toast ngx-toastr",
              closeButton: true,
            });
            this.passwordResetComplete.emit(this.username);
          }, 3000);
        },
        error: (err) => {
          this.loading = false;
          this.error.body = err.error?.message || "An error occured. Please try again";
        },
      });
  }

  togglePasswordTextType(): void {
    this.passwordTextType = !this.passwordTextType;
  }

  togglePasswordTextType2(): void {
    this.passwordTextType2 = !this.passwordTextType2;
  }

  launchHelpModal(): void {
    const modalRef = this.modalService.open(RegistrationHelpPageComponent, {
      size: "sm",
      keyboard: false,
    });
    modalRef.componentInstance.helpType = "login";
  }

  showSuccess(msg: string): void {
    this.toastr.success(`${msg}`, "Success!", {
      timeOut: 5000,
      toastClass: "toast ngx-toastr",
      closeButton: true,
    });
  }
}